import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { TitleProps, Title } from "./Title";
import { SimpleRichTextProps, SimpleRichText } from "./SimpleRichText";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import {
  IconProps,
  ImageWithAspectRatioProps,
  CardBackgroundColorType,
} from "./types";

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className={className}
  >
    <path
      d="M6.06197 23.8684C5.57639 23.8684 5.09081 23.7428 4.60523 23.3659C3.87687 22.7378 3.51268 21.8585 3.63408 20.8535L4.60523 15.326L0.720608 11.4317C-0.00775915 10.8036 -0.129154 9.79862 0.113635 8.79363C0.356424 7.91427 1.20619 7.16053 2.05595 7.0349L7.39731 6.28116L9.8252 1.25623C10.1894 0.628116 11.0391 0 12.0103 0C12.9815 0 13.8312 0.502493 14.1954 1.38186L16.6233 6.40679L21.9647 7.16053C22.9358 7.28615 23.6642 7.91427 23.907 8.91925C24.1497 9.79862 23.907 10.8036 23.3 11.5573L19.4154 15.4517L20.3865 20.9791C20.5079 21.9841 20.1437 22.8634 19.4154 23.4916C18.687 24.1197 17.7158 24.1197 16.8661 23.7428L12.0103 20.9791L7.27591 23.6172C6.91173 23.8684 6.54755 23.8684 6.06197 23.8684Z"
      fill="#499B45"
    />
  </svg>
);

export const cardTestimonial = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:h-full uipm:gap-[var(--padding-padding-m)]",
    "uipm:font-normal",
    "uipm:rounded-none uipm:md:rounded-[var(--radius-lg)]"
  ),
  variants: {
    backgroundColor: {
      primary: "uipm:bg-[var(--surface-surface-accent)]",
      secondary: "uipm:bg-[var(--surface-surface-accent-secondary)]",
      "secondary-light": "uipm:bg-[var(--surface-surface-accent-light)]",
      light: "uipm:bg-[var(--surface-surface-secondary)]",
      "very-light": "uipm:bg-[#F7F7F7]",
      white: "uipm:bg-[var(--surface-surface-primary)]",
      none: "uipm:bg-transparent",
    },
    verticalPadding: {
      default: "uipm:py-8 uipm:md:py-[var(--padding-padding-m)]",
      small: "uipm:py-[var(--container-card-padding)]",
      medium: "uipm:py-[var(--container-card-medium-padding)]",
      large: "uipm:py-[var(--container-card-large-padding)]",
    },
    horizontalPadding: {
      default: "uipm:px-4 uipm:md:px-[var(--padding-padding-m)]",
      small: "uipm:px-[var(--container-card-padding)]",
      medium: "uipm:px-[var(--container-card-medium-padding)]",
      large: "uipm:px-[var(--container-card-large-padding)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
    verticalPadding: "default",
    horizontalPadding: "default",
  },
});

const dateWrapper = tv({
  base: "uipm:text-[var(--typography-sizes-body-medium)] uipm:leading-[var(--typography-line-heights-body-medium)]",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted)]",
      secondary: "uipm:text-[var(--typography-colors-text-heading)]",
      "secondary-light": "uipm:text-[var(--typography-colors-text-heading)]",
      light: "uipm:text-[var(--surface-surface-accent)]",
      "very-light": "uipm:text-[var(--surface-surface-accent)]",
      white: "uipm:text-[var(--typography-colors-text-heading)]",
      none: "uipm:text-[var(--surface-surface-accent)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

const testimonialTextWrapper = tv({
  base: "uipm:font-normal",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted-faded)]",
      secondary: "uipm:text-[var(--typography-colors-text-accent)]",
      "secondary-light": "uipm:text-[var(--typography-colors-text-accent)]",
      light: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      "very-light": "uipm:text-[var(--typography-colors-text-faded-dark)]",
      white: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      none: "uipm:text-[var(--typography-colors-text-faded-dark)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

export interface CardTestimonialProps
  extends Omit<ComponentProps<"div">, "title"> {
  name?: TitleProps | null;
  date?: string;
  starsAmount?: number;
  text?: SimpleRichTextProps | null;
  icon?: ImageWithAspectRatioProps | null;
  backgroundColor?: CardBackgroundColorType;
  verticalPadding?: "default" | "small" | "medium" | "large";
  horizontalPadding?: "default" | "small" | "medium" | "large";
}

export const CardTestimonial: React.FC<CardTestimonialProps> = ({
  name,
  date,
  starsAmount = 5,
  text,
  icon,
  backgroundColor = "primary",
  verticalPadding = "default",
  horizontalPadding = "default",
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        cardTestimonial({
          backgroundColor,
          verticalPadding,
          horizontalPadding,
        }),
        className
      )}
      {...props}
    >
      {/* First Section - User Info */}
      <div className="uipm:flex uipm:items-center uipm:gap-[var(--margin-margin-default)]">
        {/* Icon */}
        {icon && (
          <div
            className={cn(
              "uipm:w-[38px] uipm:h-[38px] uipm:flex-shrink-0",
              "uipm:md:w-[58px] uipm:md:h-[58px]"
            )}
          >
            <ImageWithAspectRatio
              {...icon}
              sizes="(max-width: 768px) 38px, 58px"
            />
          </div>
        )}

        {/* User Details */}
        <div className="uipm:flex uipm:flex-col uipm:flex-1">
          {/* Name */}
          {name && <Title {...name} className={name.className} />}

          {/* Date */}
          {date && (
            <div className={dateWrapper({ backgroundColor })}>{date}</div>
          )}

          {/* Stars */}
          {starsAmount > 0 && (
            <div
              className={cn(
                "uipm:flex uipm:items-center uipm:gap-[10px]",
                "uipm:md:mt-2"
              )}
            >
              {Array.from({ length: starsAmount }, (_, index) => (
                <StarIcon key={index} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Second Section - Testimonial Text */}
      {text && (
        <div className={testimonialTextWrapper({ backgroundColor })}>
          <SimpleRichText {...text} />
        </div>
      )}
    </div>
  );
};

export type CardTestimonialVariants = VariantProps<typeof cardTestimonial>;
